import { getMonthTermEndDate, getServiceDurationInMonths } from "./consts";
import { toLocalDate, toDateOnlyISO } from "@/utils/datetime";
import { TFunction } from "i18next";
import {
  ServiceItem,
  ServiceItemDetail,
  ServiceDuration,
  PricingModel
} from "@/pages/settings/pages/products-v2/types";
import {
  getBillingFrequencyLabel,
  getBillingPeriodLabel,
  getServiceDurationLabel
} from "./consts";
import { PricingOption } from "./types";
import { ContractRevenueItem } from "@/pages/contracts/types";

export const computeContractDateRange = (
  revenueItems: ContractRevenueItem[],
  contractStartDate: string | null | undefined,
  contractEndDate: string | null | undefined
) => {
  const startDates = revenueItems
    .map((item) => item.serviceStartDate)
    .filter((date): date is NonNullable<typeof date> => !!date);
  const endDates = revenueItems
    .map((item) => item.serviceEndDate)
    .filter((date): date is NonNullable<typeof date> => !!date);

  const start = startDates.length
    ? startDates.reduce((min, currentDate) =>
        currentDate < min ? currentDate : min
      )
    : (contractStartDate ?? null);
  const end = endDates.length
    ? endDates.reduce((max, currentDate) =>
        currentDate > max ? currentDate : max
      )
    : (contractEndDate ?? null);

  if (!start || !end) return null;
  return { start, end };
};

// The end date a term of `months` reaches from `startDate`.
export const serviceEndDateAfterMonths = (
  startDate: string,
  months: number
): string => {
  if (!startDate) return "";
  const start = toLocalDate(startDate);
  if (isNaN(start.getTime())) return "";
  // Date-only ISO, for DateTimeField compatibility.
  return toDateOnlyISO(getMonthTermEndDate(start, months));
};

export const calculateServiceEndDate = (
  startDate: string,
  serviceItemPeriods: number | null | undefined
): string =>
  // getServiceDurationInMonths falls back to a single month for null/undefined.
  serviceEndDateAfterMonths(
    startDate,
    getServiceDurationInMonths(serviceItemPeriods)
  );

// Catalog import/cleanup can leave service items with no pricing tier and no
// pricing model set (e.g. bulk-imported parts never priced for sale). Flexible
// pricing needs no tier — everything else needs at least one — so surfacing
// these in the add-service picker leads to a dead-end drawer with nothing
// selectable and no submit path.
export const hasConfiguredPricing = (
  service: Pick<ServiceItem, "pricingModel" | "pricing">
): boolean =>
  service.pricingModel === PricingModel.FLEXIBLE || service.pricing.length > 0;

export const generatePricingOptions = (
  selectedService: ServiceItemDetail | null,
  t: TFunction
): PricingOption[] => {
  if (!selectedService) return [];

  return selectedService.pricing.map((pricing) => {
    const isCustomDuration =
      pricing.serviceItemPeriods === ServiceDuration.CUSTOM;

    if (isCustomDuration) {
      return {
        id: pricing.id,
        displayName: t("contractTerms.customPricingAndBilling")
      };
    }

    const billingFrequency = getBillingFrequencyLabel(
      pricing.billingFrequency,
      t
    );
    const durationLabel = getServiceDurationLabel(
      pricing.serviceItemPeriods,
      t
    );
    const period = getBillingPeriodLabel(pricing.billingFrequency, t);

    return {
      id: pricing.id,
      displayName: `${durationLabel} ${t("contractTerms.periodBilled", { frequency: billingFrequency.toLowerCase() })}`,
      subtitle: `$${pricing.price} ${t("contractTerms.perUnit")}/${period}`
    };
  });
};
